import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  ArrowLeft,
  ArrowRight,
  GraduationCap,
  Rocket,
  Shield,
  User,
  UsersRound,
} from 'lucide-react'

import { useAuthStore } from '@/store/auth.store'
import { UserRole } from '@/types'
import { ROUTES } from '@/constants'
import { cn } from '@/utils/cn'
import { AuthLogo } from '@/components/auth-logo'
import { Button, FormInput } from '@/components'

type LoginRole = 'student' | 'parent' | 'teacher' | 'admin'

const studentLoginSchema = z.object({
  studentCode: z
    .string()
    .min(1, 'Student code is required')
    .min(3, 'Student code must be at least 3 characters'),
  pin: z
    .string()
    .min(1, 'PIN is required')
    .min(4, 'PIN must be at least 4 characters'),
})

const emailLoginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
})

type StudentLoginValues = z.infer<typeof studentLoginSchema>
type EmailLoginValues = z.infer<typeof emailLoginSchema>
type LoginFormValues = StudentLoginValues & EmailLoginValues

const ROLE_TABS: { id: LoginRole; label: string; icon: React.ReactNode }[] = [
  {
    id: 'student',
    label: 'Student',
    icon: <User className="w-5 h-5 shrink-0" />,
  },
  {
    id: 'parent',
    label: 'Parent',
    icon: <UsersRound className="w-5 h-5 shrink-0" />,
  },
  {
    id: 'teacher',
    label: 'Teacher',
    icon: <GraduationCap className="w-5 h-5 shrink-0" />,
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: <Shield className="w-5 h-5 shrink-0" />,
  },
]

const ROLE_CONTENT: Record<
  LoginRole,
  {
    heading: string
    subtitle: string
    showRobot: boolean
    buttonText: string
    buttonIcon: React.ReactNode
    buttonVariant: 'primary' | 'secondary' | 'outline' | 'ghost'
    buttonClassName?: string
    /** Active tab text/icon color – matches this role’s button background */
    activeTabClass: string
  }
> = {
  student: {
    heading: 'Welcome Back, Young Coder! 🚀',
    subtitle: 'Enter your code to continue coding adventure',
    showRobot: true,
    buttonText: 'Login & Learn!',
    buttonIcon: <Rocket className="w-5 h-5" />,
    buttonVariant: 'primary',
    activeTabClass: 'text-[#F28C28]',
  },
  parent: {
    heading: 'Parent Login',
    subtitle: "Manage your children's learning journey",
    showRobot: false,
    buttonText: 'Login',
    buttonIcon: <ArrowRight className="w-5 h-5" />,
    buttonVariant: 'primary',
    buttonClassName: 'bg-[#34A853] hover:bg-green-600 focus:ring-portal-green',
    activeTabClass: 'text-[#34A853]',
  },
  teacher: {
    heading: 'Teacher Login',
    subtitle: 'Access your classes and student progress',
    showRobot: false,
    buttonText: 'Login',
    buttonIcon: <ArrowRight className="w-5 h-5" />,
    buttonVariant: 'primary',
    activeTabClass: 'text-[#F28C28]',
  },
  admin: {
    heading: 'Admin Login',
    subtitle: 'Manage your organization',
    showRobot: false,
    buttonText: 'Login',
    buttonIcon: <ArrowRight className="w-5 h-5" />,
    buttonVariant: 'primary',
    buttonClassName: 'bg-[#1D5AA6] hover:bg-primary-900 focus:ring-primary-700',
    activeTabClass: 'text-[#1D5AA6]',
  },
}

const SUPPORT_EMAIL = 'support@codeambassadorsng@gmail.com'

const defaultValues: LoginFormValues = {
  studentCode: '',
  pin: '',
  email: '',
  password: '',
}

export const Login: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState<LoginRole>('student')
  const [serverError, setServerError] = useState('')

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues,
    mode: 'onTouched',
  })

  const { login, setUser, setToken, isLoading } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()

  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ||
    ROUTES.DASHBOARD

  const onRoleChange = (role: LoginRole) => {
    setSelectedRole(role)
    clearErrors()
    setServerError('')
  }

  const onSubmit = handleSubmit(async data => {
    setServerError('')

    if (selectedRole === 'student') {
      const result = studentLoginSchema.safeParse({
        studentCode: data.studentCode,
        pin: data.pin,
      })
      if (!result.success) {
        result.error.errors.forEach(err => {
          const path = err.path[0] as keyof StudentLoginValues
          setError(path, { message: err.message })
        })
        return
      }
      try {
        await login({
          email: result.data.studentCode,
          password: result.data.pin,
        })
        navigate(from, { replace: true })
      } catch (err) {
        setServerError(
          err instanceof Error ? err.message : 'Login failed. Please try again.'
        )
      }
    } else {
      const result = emailLoginSchema.safeParse({
        email: data.email,
        password: data.password,
      })
      if (!result.success) {
        result.error.errors.forEach(err => {
          const path = err.path[0] as keyof EmailLoginValues
          setError(path, { message: err.message })
        })
        return
      }
      if (selectedRole === 'parent') {
        setUser({
          id: 'parent-mock-1',
          email: result.data.email,
          firstName: 'Mrs.',
          lastName: 'Ada',
          role: UserRole.PARENT,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })
        setToken('mock-parent-token')
        navigate(ROUTES.PARENT.DASHBOARD, { replace: true })
        return
      }
      try {
        await login({
          email: result.data.email,
          password: result.data.password,
        })
        navigate(from, { replace: true })
      } catch (err) {
        setServerError(
          err instanceof Error ? err.message : 'Login failed. Please try again.'
        )
      }
    }
  })

  const content = ROLE_CONTENT[selectedRole]
  const isStudent = selectedRole === 'student'

  return (
    <div className="min-h-screen flex flex-col items-center bg-white py-8 px-4  md:mt-10">
      <Link
        to="/"
        className="flex items-center gap-2 text-[#222222] text-base font-normal mb-6 hover:text-gray-700"
      >
        <ArrowLeft className="w-4 h-4 shrink-0" />
        Back to Home
      </Link>
      <AuthLogo className="h-14 w-auto" alt="Code Ambassadors" />
      <p className="text-[#222222] text-base font-normal mb-8 mt-4">
        Sign in to continue your coding journey
      </p>

      <div className="w-full max-w-2xl bg-white rounded-2xl space-y-8 shadow-lg border border-gray-100">
        <div className="flex gap-1 p-2 bg-[#F3F4F6] rounded-2xl mb-4">
          {ROLE_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => onRoleChange(tab.id)}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-[14px] font-medium transition-colors',
                selectedRole === tab.id
                  ? 'bg-white shadow-sm ' + ROLE_CONTENT[tab.id].activeTabClass
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {content.showRobot && (
          <div className="flex justify-center">
            <img
              src="/images/auth_bot.png"
              alt=""
              className="h-[180px] w-auto object-contain"
            />
          </div>
        )}

        <div>
          <h1 className="text-xl sm:text-2xl font-medium text-[#222222] text-center mb-1 px-6 pt-2">
            {content.heading}
          </h1>
          <p className="text-[#717392] text-base font-normal text-center mb-6 px-6">
            {content.subtitle}
          </p>
        </div>

        <form className="space-y-5 p-6 pt-0" onSubmit={onSubmit}>
          {serverError && (
            <div className="rounded-lg bg-red-50 border border-red-100 p-3">
              <p className="text-sm text-red-700">{serverError}</p>
            </div>
          )}

          {isStudent ? (
            <>
              <FormInput
                {...register('studentCode')}
                id="studentCode"
                label="Student Code"
                type="text"
                autoComplete="username"
                required
                placeholder="e.g., STUDENT123"
                error={errors.studentCode?.message}
              />
              <FormInput
                {...register('pin')}
                id="pin"
                label="PIN"
                type="password"
                autoComplete="current-password"
                required
                placeholder="****"
                helperText="Ask your parent or teacher if you forgot your PIN"
                error={errors.pin?.message}
              />
            </>
          ) : (
            <>
              <FormInput
                {...register('email')}
                id="email"
                label="Email Address"
                type="email"
                autoComplete="email"
                required
                placeholder="you@example.com"
                error={errors.email?.message}
              />
              <div>
                <FormInput
                  {...register('password')}
                  id="password"
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  required
                  placeholder="********"
                  error={errors.password?.message}
                />
                <div className="flex justify-end mt-1.5">
                  <Link
                    to={ROUTES.FORGOT_PASSWORD}
                    className={cn(
                      'text-sm font-medium',
                      selectedRole === 'parent'
                        ? 'text-portal-green hover:underline'
                        : selectedRole === 'admin'
                          ? 'text-primary-600 hover:underline'
                          : 'text-portal-orange hover:underline'
                    )}
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>
            </>
          )}

          <div className={cn(selectedRole !== 'parent' && 'pb-10 pt-5')}>
            <Button
              type="submit"
              fullWidth
              variant={content.buttonVariant}
              icon={content.buttonIcon}
              iconPosition="left"
              loading={isLoading}
              loadingLabel="Signing in..."
              className={content.buttonClassName}
            >
              {content.buttonText}
            </Button>
          </div>

          {selectedRole === 'parent' && (
            <p className="text-center text-sm text-gray-600 pt-2 pb-10">
              Don&apos;t have an account?{' '}
              <Link
                to={ROUTES.REGISTER}
                className="font-normal text-[#34A853] hover:underline"
              >
                Sign Up here
              </Link>
            </p>
          )}
        </form>
      </div>

      {selectedRole !== 'student' && (
        <p className="text-center text-sm text-[#717392] mt-8">
          Need help? Contact{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="font-normal text-portal-green hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>
        </p>
      )}
    </div>
  )
}
